import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useNavigate, useParams } from "react-router-dom";
import {
  collection,
  addDoc,
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../services/firebase";
import { useAuth } from "../../contexts/AuthContext";
import toast from "react-hot-toast";
import ReactMarkdown from "react-markdown";
import MarkdownEditor from "../../components/common/MarkdownEditor";
import { format } from "date-fns";

const Container = styled.div`
  max-width: var(--max-width);
  margin: 0 auto;
  padding: 3rem 2rem;
`;

const Title = styled.h1`
  margin-bottom: 2rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 500;
  color: var(--color-text);
`;

const HelpText = styled.small`
  color: var(--color-text-light);
  font-size: 0.875rem;
  margin-top: -0.25rem;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 2px solid var(--color-border);
  border-radius: 6px;
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: var(--color-accent);
  }
`;

const TextArea = styled.textarea`
  padding: 0.75rem;
  border: 2px solid var(--color-border);
  border-radius: 6px;
  font-size: 1rem;
  line-height: 1.6;
  min-height: 150px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: var(--color-accent);
  }
`;

const ContentTextArea = styled(TextArea)`
  min-height: 400px;
  font-family: "Monaco", "Consolas", monospace;
  font-size: 0.9rem;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
  justify-content: flex-end;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SubmitButton = styled(Button)`
  background: var(--color-accent);
  color: white;
  border: none;

  &:hover:not(:disabled) {
    background: var(--color-accent-hover);
  }
`;

const CancelButton = styled(Button)`
  background: transparent;
  color: var(--color-text);
  border: 2px solid var(--color-border);

  &:hover:not(:disabled) {
    background: var(--color-bg);
  }
`;

const PreviewButton = styled(Button)`
  background: transparent;
  color: var(--color-accent);
  border: 2px solid var(--color-accent);

  &:hover:not(:disabled) {
    background: var(--color-accent);
    color: white;
  }
`;

const PreviewModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 2rem;
`;

const PreviewContent = styled.div`
  background: var(--color-white);
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  border-radius: 10px;
  padding: 3rem;
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--color-text-light);
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;

  &:hover {
    background: var(--color-bg);
    color: var(--color-text);
  }
`;

const PreviewTitle = styled.h1`
  margin-bottom: 0.5rem;
`;

const PreviewMeta = styled.div`
  color: var(--color-text-light);
  margin-bottom: 2rem;
  font-size: 0.9rem;
`;

const PreviewExcerpt = styled.p`
  font-size: 1.25rem;
  color: var(--color-text-light);
  margin-bottom: 2rem;
  line-height: 1.6;
  font-style: italic;
`;

const PreviewThumbnail = styled.img`
  width: 100%;
  max-height: 400px;
  object-fit: cover;
  border-radius: 8px;
  margin-bottom: 2rem;
`;

const PreviewBody = styled.div`
  line-height: 1.8;
  font-size: 1.1rem;

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: 2rem 0 1rem;
  }

  p {
    margin-bottom: 1.5rem;
  }

  ul,
  ol {
    margin-bottom: 1.5rem;
    padding-left: 2rem;
  }

  blockquote {
    border-left: 4px solid var(--color-accent);
    padding-left: 1rem;
    margin: 1.5rem 0;
    font-style: italic;
    color: var(--color-text-light);
  }

  code {
    background: var(--color-bg);
    padding: 0.2rem 0.4rem;
    border-radius: 3px;
    font-family: "Monaco", "Consolas", monospace;
    font-size: 0.9em;
  }

  pre {
    background: var(--color-bg);
    padding: 1rem;
    border-radius: 6px;
    overflow-x: auto;
    margin-bottom: 1.5rem;

    code {
      background: none;
      padding: 0;
    }
  }
`;

const NewPost = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { id } = useParams();
  const isEditMode = !!id;
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(isEditMode);
  const [showPreview, setShowPreview] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    excerpt: "",
    content: "",
    category: "",
    thumbnail: "",
  });

  // Fetch existing post data if in edit mode
  useEffect(() => {
    if (isEditMode) {
      const fetchPost = async () => {
        try {
          const docRef = doc(db, "posts", id);
          const docSnap = await getDoc(docRef);

          if (docSnap.exists()) {
            const postData = docSnap.data();
            setFormData({
              title: postData.title || "",
              excerpt: postData.excerpt || "",
              content: postData.content || "",
              category: postData.category || "",
              thumbnail: postData.thumbnail || "",
            });
          } else {
            toast.error("Post not found");
            navigate("/");
          }
        } catch (error) {
          console.error("Error fetching post:", error);
          toast.error("Failed to load post");
          navigate("/");
        } finally {
          setLoading(false);
        }
      };

      fetchPost();
    }
  }, [id, isEditMode, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePreview = () => {
    setShowPreview(true);
  };

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && showPreview) {
        setShowPreview(false);
      }
    };

    if (showPreview) {
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [showPreview]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentUser) {
      toast.error("You must be logged in to create posts");
      return;
    }

    setSubmitting(true);
    try {
      if (isEditMode) {
        // Update existing post
        const docRef = doc(db, "posts", id);
        await updateDoc(docRef, {
          ...formData,
          updatedAt: serverTimestamp(),
        });
        toast.success("Post updated successfully!");
        navigate(`/blog/${id}`);
      } else {
        // Create new post
        const docRef = await addDoc(collection(db, "posts"), {
          ...formData,
          createdAt: serverTimestamp(),
          author: {
            uid: currentUser.uid,
            displayName: currentUser.displayName,
            photoURL: currentUser.photoURL,
          },
        });
        toast.success("Post created successfully!");
        navigate(`/blog/${docRef.id}`);
      }
    } catch (error) {
      console.error(
        `Error ${isEditMode ? "updating" : "creating"} post:`,
        error
      );
      toast.error(`Failed to ${isEditMode ? "update" : "create"} post`);
    } finally {
      setSubmitting(false);
    }
  };

  if (!currentUser) {
    return (
      <Container>
        <p>You must be logged in to {isEditMode ? "edit" : "create"} posts.</p>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container>
        <p>Loading post...</p>
      </Container>
    );
  }

  return (
    <Container>
      <Title>{isEditMode ? "Edit Post" : "New Post"}</Title>

      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <Label htmlFor="title">Title</Label>
          <Input
            id="title"
            name="title"
            type="text"
            value={formData.title}
            onChange={handleChange}
            required
            disabled={submitting}
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="category">Category</Label>
          <Input
            id="category"
            name="category"
            type="text"
            value={formData.category}
            onChange={handleChange}
            placeholder="e.g., Essay, Anthropology, Philosophy"
            disabled={submitting}
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="thumbnail">Thumbnail Image URL</Label>
          <Input
            id="thumbnail"
            name="thumbnail"
            type="url"
            value={formData.thumbnail}
            onChange={handleChange}
            placeholder="https://example.com/image.jpg"
            disabled={submitting}
          />
          <HelpText>
            Paste an image URL from any source (e.g., Unsplash, Pexels, your
            CDN)
          </HelpText>
        </FormGroup>

        <FormGroup>
          <Label htmlFor="excerpt">Excerpt</Label>
          <TextArea
            id="excerpt"
            name="excerpt"
            value={formData.excerpt}
            onChange={handleChange}
            placeholder="A brief description of your post..."
            required
            disabled={submitting}
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="content">Content (Markdown supported)</Label>
          <MarkdownEditor
            value={formData.content}
            onChange={handleChange}
            placeholder="Write your post content here..."
            disabled={submitting}
            minHeight="400px"
          />
        </FormGroup>

        <ButtonGroup>
          <CancelButton
            type="button"
            onClick={() => navigate("/")}
            disabled={submitting}
          >
            Cancel
          </CancelButton>
          <PreviewButton
            type="button"
            onClick={handlePreview}
            disabled={submitting || !formData.title || !formData.content}
          >
            Preview
          </PreviewButton>
          <SubmitButton type="submit" disabled={submitting}>
            {submitting
              ? isEditMode
                ? "Updating..."
                : "Publishing..."
              : isEditMode
              ? "Update Post"
              : "Publish Post"}
          </SubmitButton>
        </ButtonGroup>
      </Form>

      {showPreview && (
        <PreviewModal onClick={() => setShowPreview(false)}>
          <PreviewContent onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={() => setShowPreview(false)}>×</CloseButton>
            <PreviewTitle>{formData.title}</PreviewTitle>
            <PreviewMeta>
              {formData.category && `${formData.category} • `}
              {new Date().toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </PreviewMeta>
            {formData.excerpt && (
              <PreviewExcerpt>{formData.excerpt}</PreviewExcerpt>
            )}
            {formData.thumbnail && (
              <PreviewThumbnail
                src={
                  formData.thumbnail.includes("?")
                    ? `${formData.thumbnail}&tr=w-800,h-400,c-at_max,q-85`
                    : `${formData.thumbnail}?tr=w-800,h-400,c-at_max,q-85`
                }
                alt={formData.title}
              />
            )}
            <PreviewBody>
              <ReactMarkdown>{formData.content}</ReactMarkdown>
            </PreviewBody>
          </PreviewContent>
        </PreviewModal>
      )}
    </Container>
  );
};

export default NewPost;
